import SvgIcon from "@/components/common/SvgIcon";
import { useUpgradeModal } from "@/hooks/store/useUpgradeModal";
import classNames from "classnames";
import { MouseEvent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import DropUpAccount from "./dropUpAccount";
import SubMenus from "./SubMenus";
import { Menu } from "./types/Menu.type";
import useSidebarPermissions from "@/hooks/general/useSidebarPermissions";

const SideBar = () => {
  const { filterMenuItems } = useSidebarPermissions();

  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { openModal } = useUpgradeModal();

  const menuItemsBase: Omit<Menu, "isActive">[] = useMemo(
    () => [
      {
        icon: "buildingsOutline",
        activeIcon: "buildings",
        id: "organizations",
        path: "/organizations",
        isAccessible: true,
        title: t("organizations"),
      },
      {
        icon: "users",
        activeIcon: "usersGroup",
        id: "admins",
        path: "/admins",
        isAccessible: true,
        title: "Admins Center",
        subMenus: [
          {
            id: "admins-msp",
            title: "MSP Admins",
            path: "/admins",
            isSubAccessible: true,
            icon: "document-sidebar",
            parentTitle: "Admin Center",
          },
          {
            id: "admins-it",
            title: "IT Admins",
            path: "/it-admins",
            isSubAccessible: true,
            icon: "document-sidebar",
            parentTitle: "Admin Center",
          },
          {
            id: "admins-acl",
            title: "Organization Role",
            path: "/acl",
            isSubAccessible: true,
            icon: "OrganizationRoles",
            parentTitle: "Admin Center",
          },
        ],
      },
      {
        icon: "cardMoney",
        activeIcon: "cardMoneyFill",
        id: "billing",
        path: "/billing",
        isAccessible: true,
        title: "Billing",
        fillIcon: "fill-unset",
        subMenus: [
          {
            id: "billing-payment",
            title: "Payment & invoice",
            path: "/billing",
            isSubAccessible: true,
            icon: "pieChart",
            parentTitle: "Billing",
          },
          {
            id: "billing-plans",
            title: "Plans & subscriptions",
            path: "/billing/plans-subscriptions",
            isSubAccessible: true,
            icon: "billCheck2",
            parentTitle: "Billing",
          },
        ],
      },
    ],
    [t],
  );

  const getMenuIsActive = useCallback(
    (menu: Omit<Menu, "isActive">): boolean => {
      if (menu.path === "/") return pathname === "/";
      if (menu.subMenus?.length) {
        if (pathname.startsWith(menu.path)) return true;
        return menu.subMenus.some((sub) => pathname.startsWith(sub.path));
      }
      return pathname.startsWith(menu.path);
    },
    [pathname],
  );

  // 1️⃣ Filter by role  2️⃣ Attach isActive
  const menuItems: Menu[] = useMemo(() => {
    const accessible = filterMenuItems(menuItemsBase);
    return accessible.map((menu) => ({
      ...menu,
      isActive: getMenuIsActive(menu),
    }));
  }, [filterMenuItems, menuItemsBase, getMenuIsActive]);

  const renderMenuItem = useCallback(
    (menu: Menu) => {
      const handleClick = (
        e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
      ) => {
        if (menu.isFree) {
          e.preventDefault();
          openModal();
        }
      };

      if (menu.isFree)
        return (
          <div
            key={menu.id}
            onClick={handleClick}
            className="flex pb-1 flex-col gap-1 items-center transition-colors cursor-not-allowed opacity-40"
          >
            <div className="px-3 relative py-1">
              <SvgIcon
                fill="fill-gray-800"
                className="w-5 h-5"
                iconName={menu.isActive ? menu.activeIcon : menu.icon}
              />
              {menu.isBeta && (
                <div className="absolute top-[14px] -right-1 rounded-[6px] bg-blue-100 px-[4px] flex font-bold items-center text-[7px]">
                  Beta
                </div>
              )}
            </div>
            <p className="text-center font-[Lato] text-[10px] not-italic font-medium leading-[12px]">
              {menu.title}
            </p>
          </div>
        );

      return (
        <Link
          key={menu.id}
          className={classNames(
            "flex pb-1 flex-col gap-1 items-center transition-colors hover:bg-gray-100",
            { "border-s-[3px] border-blue-700": menu.isActive },
          )}
          to={menu.path}
        >
          <div className="px-3 relative py-1">
            <SvgIcon
              fill={menu.fillIcon ? menu.fillIcon : "fill-gray-800"}
              className="w-5 h-5"
              iconName={menu.isActive ? menu.activeIcon : menu.icon}
            />
            {menu.isBeta && (
              <div className="absolute top-[14px] -right-1 rounded-[6px] bg-blue-100 px-[4px] flex font-bold items-center text-[7px]">
                Beta
              </div>
            )}
          </div>
          <p className="text-center font-[Lato] text-[10px] not-italic font-medium leading-[12px]">
            {menu.title}
          </p>
        </Link>
      );
    },
    [openModal],
  );

  return (
    <div className="flex">
      <div className={classNames("w-[80px] py-1 h-full flex flex-col")}>
        <div className="flex flex-col gap-6">
          <div className="flex px-3 py-2">
            <SvgIcon
              className="m-1 h-[30px]"
              iconName="msp-logo"
              fill="fill-unset"
            />
          </div>
          <div className="flex flex-col gap-4">
            {menuItems.map(renderMenuItem)}
          </div>
        </div>
        <div className="flex-1" />
        <DropUpAccount />
      </div>
      <SubMenus menuItems={[...menuItems]} />
    </div>
  );
};

export default SideBar;
